import type { SVGProps } from "react";

export type IconName =
  | "dashboard" | "map" | "field" | "leases" | "sampling" | "bedding"
  | "analysis" | "verification" | "submittals" | "entities" | "users" | "monitoring"
  | "mortality" | "time" | "credits" | "tax" | "settings" | "menu" | "chevron" | "logout" | "share"
  | "check";

interface IconProps extends Omit<SVGProps<SVGSVGElement>, "name"> {
  name: IconName;
}

export default function Icon({ name, className = "h-5 w-5", ...props }: IconProps) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
      strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true" {...props}>
      {iconPath(name)}
    </svg>
  );
}

function iconPath(name: IconName) {
  switch (name) {
    case "dashboard": return <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="4" rx="1" /><rect x="14" y="11" width="7" height="10" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /></>;
    case "map": return <><path d="m3 6 5-3 8 3 5-3v15l-5 3-8-3-5 3Z" /><path d="M8 3v15M16 6v15" /></>;
    case "field": return <><circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3" /></>;
    case "leases": return <><path d="M5 3h10l4 4v14H5Z" /><path d="M15 3v5h5M8 12h8M8 16h6" /></>;
    case "analysis": return <><path d="M3 19h18M5 16l3-5 3 2 4-7 4 4" /><circle cx="8" cy="11" r="1" /><circle cx="15" cy="6" r="1" /></>;
    case "sampling": return <><path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 1.8 3h10.4a2 2 0 0 0 1.8-3l-5-9V3" /><path d="M8 15h8" /></>;
    case "bedding": return <><path d="M7 3h10v18l-2-1.5L12 21l-3-1.5L7 21Z" /><path d="M10 8h4M10 12h4M10 16h2" /></>;
    case "verification": return <><path d="M12 3 4.5 6v5.5c0 4.6 3.1 7.8 7.5 9.5 4.4-1.7 7.5-4.9 7.5-9.5V6Z" /><path d="m9 12 2 2 4-5" /></>;
    case "submittals": return <><path d="M4 4h16v15H4Z" /><path d="M4 14h4l2 3h4l2-3h4M9 8h6M12 5v6" /></>;
    case "entities": return <><path d="M4 21V8l8-5 8 5v13M8 21v-5h8v5M8 10h.01M12 10h.01M16 10h.01" /></>;
    case "users": return <><circle cx="9" cy="8" r="3" /><path d="M3.5 20v-2a5.5 5.5 0 0 1 11 0v2M16 4.5a3 3 0 0 1 0 5.8M17 14a5 5 0 0 1 3.5 4.8V20" /></>;
    case "monitoring": return <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M8 3v4M16 3v4M3 10h18M8 14h3M8 17h6" /></>;
    case "mortality": return <><path d="M12 3 2.8 20h18.4Z" /><path d="M12 9v5M12 17.5h.01" /></>;
    case "time": return <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>;
    case "credits": return <><circle cx="12" cy="12" r="9" /><path d="M8.5 9.5c0-1.2 1.2-2 3.5-2s3.5.8 3.5 2-1.2 2-3.5 2-3.5.8-3.5 2 1.2 2 3.5 2 3.5-.8 3.5-2M12 5.5v13" /></>;
    case "tax": return <><path d="M5 3h10l4 4v14H5Z" /><path d="M15 3v5h5M8 12h8M8 16h4" /><path d="M9 7.5h3" /></>;
    case "settings": return <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3A1.7 1.7 0 0 0 14 21v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14h-.2v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3v-.2h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9A1.7 1.7 0 0 0 21 10h.2v4H21a1.7 1.7 0 0 0-1.6 1Z" /></>;
    case "menu": return <><path d="M4 7h16M4 12h16M4 17h16" /></>;
    case "chevron": return <path d="m9 6 6 6-6 6" />;
    case "logout": return <><path d="M10 4H5v16h5M14 8l4 4-4 4M18 12H9" /></>;
    case "share": return <><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" /><path d="M12 3v12M7.5 7.5 12 3l4.5 4.5" /></>;
    case "check": return <><rect x="2.5" y="6" width="19" height="12" rx="1.5" /><path d="M6 10h5M6 14h3M13.5 14.5c1-1.5 1.6-1.5 2.2 0s1.2 1.5 2.3 0" /></>;
  }
}
